import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { ChatIcon, DeleteIcon } from "@chakra-ui/icons";
import { useAuth } from "../../context/AuthContext";
import { useForm } from "react-hook-form";
import client from "../../utils/client";
import { useState } from "react";
import { format } from "date-fns";
import { Link } from "react-router-dom";

const CommentBubble = ({ com, canDelete, isDeleting, onDelete }) => (
  <Box
    bg="gray.50"
    border="1px solid"
    borderColor="gray.100"
    borderRadius="0.75rem"
    p={3}
  >
    <Flex justifyContent="space-between" alignItems="flex-start" mb={1}>
      <Box>
        <Text fontWeight={700} fontSize="0.85rem" color="green.700">
          {com.userName}
        </Text>
        {com.createdAt && (
          <Text fontSize="0.72rem" color="gray.400">
            {format(new Date(com.createdAt), "dd/MM/yyyy 'às' HH:mm")}
          </Text>
        )}
      </Box>
      {canDelete && (
        <IconButton
          size="xs"
          icon={<DeleteIcon />}
          colorScheme="red"
          variant="ghost"
          isLoading={isDeleting}
          onClick={onDelete}
          aria-label="Remover comentário"
        />
      )}
    </Flex>
    <Text fontSize="0.9rem" color="gray.700" lineHeight={1.6}>
      {com.comment}
    </Text>
  </Box>
);

const NewsComments = ({ comments = [], newsId, setReload, reload, preview = false }) => {
  const auth = useAuth();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const isLoggedIn = !!auth.user?.id;
  const isAdmin = auth.user?.id && auth.user?.type === process.env.REACT_APP_ADMINISTRATOR;

  const onSubmit = async (data) => {
    if (!data.comment?.trim()) return;
    setSubmitError(null);
    setLoading(true);
    try {
      await client.put(`/news/addComment/${newsId}`, { comment: data.comment });
      reset();
      setReload(!reload);
    } catch {
      setSubmitError("Erro ao enviar comentário. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (commentId) => {
    setDeleteLoading(commentId);
    try {
      await client.put(`/news/removeComment/${newsId}/${commentId}`);
      setReload(!reload);
    } catch {
      alert("Erro ao remover comentário.");
    } finally {
      setDeleteLoading(null);
    }
  };

  const displayComments = preview ? comments.slice(0, 3) : comments;
  const hasMore = preview && comments.length > 3;

  return (
    <Box mt={6} pt={5} borderTop="1px solid" borderColor="gray.100">
      {/* Header */}
      <Flex alignItems="center" gap={2} mb={4}>
        <Box as={ChatIcon} color="green.500" boxSize={4} />
        <Text fontWeight={700} fontSize="1rem" color="gray.700">
          {comments.length === 0
            ? "Comentários"
            : `${comments.length} ${comments.length === 1 ? "comentário" : "comentários"}`}
        </Text>
      </Flex>

      {/* Comment list */}
      <Flex flexDir="column" gap={3}>
        {comments.length === 0 && (
          <Text color="gray.400" fontSize="0.88rem" fontStyle="italic">
            {preview
              ? "Nenhum comentário ainda."
              : "Nenhum comentário ainda. Seja o primeiro!"}
          </Text>
        )}

        {displayComments.map((com) => (
          <CommentBubble
            key={com.commentId}
            com={com}
            canDelete={isLoggedIn && (auth.user.id === com.userId || isAdmin)}
            isDeleting={deleteLoading === com.commentId}
            onDelete={() => handleDelete(com.commentId)}
          />
        ))}

        {hasMore && (
          <Link to={`/news/${newsId}`}>
            <Text
              fontSize="0.85rem"
              color="green.600"
              fontWeight={600}
              _hover={{ color: "green.800" }}
              mt={1}
            >
              Ver todos os {comments.length} comentários →
            </Text>
          </Link>
        )}
      </Flex>

      {/* Form or CTA */}
      {!preview && (
        <>
          <Divider my={5} />
          {isLoggedIn ? (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Flex flexDir="column" gap={3}>
                <Textarea
                  placeholder="Escreva seu comentário..."
                  bg="gray.50"
                  borderColor="gray.200"
                  borderRadius="0.75rem"
                  fontSize="0.95rem"
                  _focus={{ bg: "white", borderColor: "green.400" }}
                  resize="vertical"
                  {...register("comment", { required: true })}
                />
                {errors.comment && (
                  <Text color="red.500" fontSize="0.82rem">O comentário não pode estar vazio.</Text>
                )}
                {submitError && (
                  <Alert status="error" borderRadius={6}>
                    <AlertIcon />
                    {submitError}
                  </Alert>
                )}
                <Button
                  type="submit"
                  alignSelf="flex-end"
                  colorScheme="green"
                  size="sm"
                  px={6}
                  isLoading={loading}
                >
                  Comentar
                </Button>
              </Flex>
            </form>
          ) : (
            <Box
              bg="green.50"
              borderRadius="0.75rem"
              p={4}
              textAlign="center"
              border="1px solid"
              borderColor="green.100"
            >
              <Text color="gray.600" fontSize="0.9rem">
                <Link to="/login" style={{ color: "#1a8a49", fontWeight: 700 }}>
                  Faça login
                </Link>{" "}
                para deixar um comentário.
              </Text>
            </Box>
          )}
        </>
      )}

      {/* Preview CTA: invite to comment */}
      {preview && (
        <Box mt={4}>
          <Link to={`/news/${newsId}`}>
            <Box
              bg="green.50"
              borderRadius="0.75rem"
              p={3}
              textAlign="center"
              border="1px dashed"
              borderColor="green.200"
              _hover={{ bg: "green.100" }}
              transition="all 0.15s"
            >
              <Text color="green.600" fontSize="0.85rem" fontWeight={600}>
                💬 {isLoggedIn ? "Clique aqui para comentar" : "Faça login e comente"}
              </Text>
            </Box>
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default NewsComments;
